/**
 * @file index.tsx
 *
 * @fileoverview Renders Watchado reals.
 */
import Script from 'next/script';
import { useCallback, useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { useConfig } from '~/jobiqo/context/config';
import styles from './watchado.module.scss';

declare global {
  interface Window {
    WcdReals: {
      init: (config: {
        selector: string;
        platform: string;
        jobIds: string[];
        idType: string;
        onOpen: (path: string) => void;
        onChange: (path: string) => void;
        onClose: () => void;
      }) => void;
      close: () => void;
      unbind: () => void;
      update: (config: { jobIds: string[] }) => void;
    };
  }
}

interface WatchadoProps {
  /**
   * The job IDs to initialize or update Watchado with.
   */
  watchadoJobIds: string[];
}

export default function WatchadoReals({ watchadoJobIds = [] }: WatchadoProps) {
  const { getConfigByName } = useConfig();
  const router = useRouter();
  const [isInitialized, setIsInitialized] = useState(false);

  const WATCHADO_PLATFORM_ID: string = (getConfigByName('platform_id')
    ?.value[0] as unknown) as string;

  const initializeWatchado = async () => {
    if (window?.WcdReals) {
      await window.WcdReals.init({
        selector: '#wcd-root',
        platform: WATCHADO_PLATFORM_ID,
        jobIds: watchadoJobIds.map((jobId) => {
          return `${WATCHADO_PLATFORM_ID}-${jobId}`;
        }),
        idType: 'remote',
        onOpen: (path) => console.log('OPEN', path),
        onChange: (path) => console.log('CHANGE', path),
        onClose: () => console.log('CLOSE'),
      });
      setIsInitialized(true);
    }
  };

  const handleRouteChange = useCallback(() => {
    if (window?.WcdReals) {
      if (isInitialized) {
        // If already initialized, just update
        window.WcdReals.update({
          jobIds: watchadoJobIds.map((jobId) => {
            return `${WATCHADO_PLATFORM_ID}-${jobId}`;
          }),
        });
      } else {
        // If not initialized yet, initialize first
        initializeWatchado();
      }
    }
  }, [watchadoJobIds, WATCHADO_PLATFORM_ID, isInitialized]);

  // Handle route changes
  useEffect(() => {
    // Listen for route change completion
    router.events.on('routeChangeComplete', handleRouteChange);
    return () => {
      router.events.off('routeChangeComplete', handleRouteChange);
    };
  }, [router.events, handleRouteChange]);

  const handleScriptLoad = useCallback(() => {
    initializeWatchado();
  }, [initializeWatchado]);

  if (!WATCHADO_PLATFORM_ID) {
    return null;
  }

  const hasJobs = watchadoJobIds.length > 0;

  return (
    <div
      className={`${styles.watchado} ${hasJobs ? styles.watchadoReserved : ''}`}
    >
      <div id="wcd-root"></div>
      <Script
        id="wcd-script"
        src="https://scripts.whatchado.com/wcd.reals.js"
        strategy="lazyOnload"
        onLoad={handleScriptLoad}
      />
    </div>
  );
}

export { WatchadoReals };
